import i18n from "i18next";

/**
 * Apple Pencil in tekstvelden (alle zoek- en invoervelden van de app).
 *
 * 1. Scribble maakt van een tik met de pen soms losse leestekens (". : -") in het
 *    veld. Een invoer die na een pen-aanraking alléén uit zulke tekens bestaat,
 *    wordt teruggedraaid. Echt handschrift (letters/cijfers) blijft ongemoeid.
 * 2. Pas als de pen het veld aanraakte, verschijnen er twee kleine knoppen (⌫ en ✕)
 *    bij dat veld, voor wissen zonder toetsenbord. Vinger of muis laat ze verdwijnen.
 */

type Field = HTMLInputElement | HTMLTextAreaElement;

const LOS_TEKEN = /[.:;,\-–—·•'"`´_|!]+/g;
const TEKST_INPUT = new Set(["text", "search", "email", "url", "tel", "", "number"]);

function veld(t: EventTarget | null): Field | null {
  const el = (t as Element | null)?.closest?.("input, textarea") as Field | null;
  if (!el) return null;
  if (el instanceof HTMLInputElement && !TEKST_INPUT.has(el.type)) return null;
  if (el.readOnly || el.disabled) return null;
  return el;
}

function zetWaarde(el: Field, v: string) {
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement : HTMLInputElement;
  Object.getOwnPropertyDescriptor(proto.prototype, "value")?.set?.call(el, v);
  el.dispatchEvent(new Event("input", { bubbles: true }));
}

export function installPenInput(): () => void {
  let actief: Field | null = null; // veld dat de pen aanraakte
  let voor = "";
  let laatstePen = 0;
  let bezig = false;
  let balk: HTMLDivElement | null = null;
  let raf = 0;

  const verberg = () => {
    balk?.remove();
    balk = null;
    cancelAnimationFrame(raf);
    actief = null;
  };

  const plaats = () => {
    if (!balk || !actief?.isConnected) {
      verberg();
      return;
    }
    const r = actief.getBoundingClientRect();
    const top = r.top >= 26 ? r.top - 24 : r.bottom + 2;
    balk.style.left = `${Math.max(4, r.right - 52)}px`;
    balk.style.top = `${top}px`;
  };
  // Geen rAF-lus: elke frame getBoundingClientRect forceert layout, en dat maakt
  // de hele pagina traag bij een zware DOM. Alleen bij scroll/resize bijwerken.
  const herplaats = () => {
    if (!balk) return;
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(plaats);
  };

  const toon = (el: Field) => {
    if (balk && actief === el) return;
    verberg();
    actief = el;
    balk = document.createElement("div");
    balk.style.cssText =
      "position:fixed;z-index:2147483646;display:flex;gap:2px;font:11px/1 system-ui;" +
      "-webkit-user-select:none;user-select:none;touch-action:none";
    const knop = (txt: string, label: string, actie: () => void) => {
      const b = document.createElement("button");
      b.type = "button";
      b.textContent = txt;
      b.setAttribute("aria-label", label);
      b.style.cssText =
        "min-width:22px;height:20px;padding:0 3px;border-radius:4px;border:1px solid #64748b;" +
        "background:#1e293b;color:#fff;cursor:pointer";
      const stop = (e: Event) => e.preventDefault(); // veld houdt focus
      b.addEventListener("pointerdown", stop);
      b.addEventListener("mousedown", stop);
      b.addEventListener("click", () => {
        if (actief) {
          actie();
          actief.focus();
        }
      });
      return b;
    };
    balk.append(
      knop("⌫", i18n.t("canvas.penInput.deleteChar"), () => {
        if (actief) zetWaarde(actief, actief.value.slice(0, -1));
      }),
      knop("✕", i18n.t("canvas.penInput.clearField"), () => {
        if (actief) zetWaarde(actief, "");
      }),
    );
    document.body.appendChild(balk);
    plaats();
  };

  const penAan = (t: EventTarget | null) => {
    const el = veld(t);
    if (!el) return;
    laatstePen = Date.now();
    voor = el.value;
    toon(el);
  };

  // Een aanraking van de knoppen mag geen "buiten geklikt" zijn voor panelen en
  // menu's (het attributenpaneel sloot zichzelf): hier in de capture-fase van
  // window stoppen, vóór hun eigen luisteraars. De knoppen reageren op `click`.
  const bijKnoppen = (e: Event) => {
    if (!balk?.contains(e.target as Node)) return false;
    e.preventDefault(); // het veld houdt de focus
    e.stopPropagation();
    return true;
  };
  const onMouseDown = (e: MouseEvent) => {
    bijKnoppen(e);
  };
  const onPointerDown = (e: PointerEvent) => {
    if (bijKnoppen(e)) return;
    if (e.pointerType === "pen") {
      if (veld(e.target)) penAan(e.target);
      else verberg();
    } else verberg();
  };
  const onTouchStart = (e: TouchEvent) => {
    const t = e.touches[0] as (Touch & { touchType?: string }) | undefined;
    if (t?.touchType === "stylus") penAan(e.target);
  };
  const onInput = (e: Event) => {
    if (bezig) return;
    const el = veld(e.target);
    if (!el || el !== actief || Date.now() - laatstePen > 1500) return;
    const nu = el.value;
    if (nu === voor) return;
    // Alleen losse leestekens erbij gekomen? Dan was het een tik, geen handschrift.
    if (nu.replace(LOS_TEKEN, "") === voor.replace(LOS_TEKEN, "") && nu.length > voor.length) {
      bezig = true;
      zetWaarde(el, voor);
      bezig = false;
    } else {
      voor = nu;
    }
  };
  const onFocusOut = (e: FocusEvent) => {
    if (veld(e.target) !== actief) return;
    window.setTimeout(() => {
      if (document.activeElement !== actief) verberg();
    }, 200);
  };

  window.addEventListener("pointerdown", onPointerDown, true);
  window.addEventListener("mousedown", onMouseDown, true);
  window.addEventListener("touchstart", onTouchStart, { capture: true, passive: true });
  window.addEventListener("input", onInput, true);
  window.addEventListener("focusout", onFocusOut, true);
  window.addEventListener("scroll", herplaats, { capture: true, passive: true });
  window.addEventListener("resize", herplaats, { passive: true });
  return () => {
    window.removeEventListener("pointerdown", onPointerDown, true);
    window.removeEventListener("mousedown", onMouseDown, true);
    window.removeEventListener("touchstart", onTouchStart, true);
    window.removeEventListener("input", onInput, true);
    window.removeEventListener("focusout", onFocusOut, true);
    window.removeEventListener("scroll", herplaats, true);
    window.removeEventListener("resize", herplaats);
    verberg();
  };
}
